// domain/tren — deret waktu untuk grafik Tren di Dashboard.
//
// Semua perhitungannya di sini, murni dan teruji, karena grafik gampang berbohong: satu salah
// bucket atau satu pembagian nol yang digambar sebagai 0 sudah cukup membuat pemilik salah
// mengambil keputusan. Lapisan tampilan hanya menggambar apa yang dikembalikan fungsi ini.
import type { Ticket } from '@core/types';
import { omzetTiket, modalTiket, piutangTiket } from './money';

export interface TiketTren extends Ticket {
  kat?: 'User' | 'Toko' | null;      // kategori pelanggan (Toko = B2B, User = retail)
  rating?: number | null;            // hasil survei kepuasan 1–5
  servisUlang?: boolean | null;      // tiket ini klaim garansi / servis ulang
}

export type MetrikId =
  | 'omzet' | 'laba' | 'margin' | 'aov' | 'piutang'
  | 'masuk' | 'selesai' | 'lama' | 'gagal' | 'ulang'
  | 'csat';
export type PecahId = 'total' | 'cabang' | 'teknisi' | 'tipe';
export type PeriodeId = 'hari' | 'minggu' | 'bulan' | 'kuartal' | 'tahun';
export type Satuan = 'rp' | 'pct' | 'angka' | 'hari' | 'skor';

export interface DefMetrik {
  id: MetrikId; label: string; satuan: Satuan;
  /** Tanggal mana yang menentukan periode: uang ikut tanggal AMBIL (kas), pekerjaan ikut tanggal TERIMA. */
  basis: 'terima' | 'ambil';
  kelompok: 'Uang' | 'Pekerjaan' | 'Mutu';
  /** Naik itu bagus? dipakai analisa untuk memilih kata & warna. */
  naikBaik: boolean;
  jelas: string;
}

/**
 * Indikator yang dipilih untuk pemilik bengkel — bukan semua yang bisa dihitung, tapi yang
 * menjawab pertanyaan yang benar-benar ia tanyakan tiap bulan:
 *   "uangnya berapa dan sehat tidak" · "kerjaannya ramai tidak & selesai tidak" · "mutunya turun tidak".
 */
export const METRIK: DefMetrik[] = [
  { id: 'omzet', label: 'Omzet', satuan: 'rp', basis: 'ambil', kelompok: 'Uang', naikBaik: true, jelas: 'Uang yang benar-benar diakui masuk (setelah Finance memverifikasi), dikurangi pengembalian dana.' },
  { id: 'laba', label: 'Laba Kotor', satuan: 'rp', basis: 'ambil', kelompok: 'Uang', naikBaik: true, jelas: 'Omzet dikurangi modal sparepart. Belum dikurangi beban operasional.' },
  { id: 'margin', label: 'Margin %', satuan: 'pct', basis: 'ambil', kelompok: 'Uang', naikBaik: true, jelas: 'Laba kotor dibagi omzet. Turunnya margin sambil omzet naik = kerja makin banyak, untung makin tipis.' },
  { id: 'aov', label: 'Rata-rata Servis', satuan: 'rp', basis: 'ambil', kelompok: 'Uang', naikBaik: true, jelas: 'Rata-rata nilai per unit yang diambil. Naik = pekerjaan makin besar atau harga membaik.' },
  { id: 'piutang', label: 'Piutang Baru', satuan: 'rp', basis: 'ambil', kelompok: 'Uang', naikBaik: false, jelas: 'Nilai yang diserahkan tapi belum lunas pada periode itu — uang yang sudah bekerja tapi belum diterima.' },
  { id: 'masuk', label: 'Unit Masuk', satuan: 'angka', basis: 'terima', kelompok: 'Pekerjaan', naikBaik: true, jelas: 'Berapa unit diterima. Ini permintaan pasar, terlepas kapan uangnya masuk.' },
  { id: 'selesai', label: 'Unit Diambil', satuan: 'angka', basis: 'ambil', kelompok: 'Pekerjaan', naikBaik: true, jelas: 'Berapa unit selesai dan diambil pelanggan.' },
  { id: 'lama', label: 'Lama Servis', satuan: 'hari', basis: 'ambil', kelompok: 'Pekerjaan', naikBaik: false, jelas: 'Rata-rata hari dari unit diterima sampai diambil. Naik = antrian menumpuk.' },
  { id: 'gagal', label: 'Servis Gagal %', satuan: 'pct', basis: 'ambil', kelompok: 'Mutu', naikBaik: false, jelas: 'Bagian unit yang berakhir tidak bisa diperbaiki atau dibatalkan.' },
  { id: 'ulang', label: 'Servis Ulang %', satuan: 'pct', basis: 'ambil', kelompok: 'Mutu', naikBaik: false, jelas: 'Bagian unit yang kembali lagi (klaim garansi). Ini cermin mutu pengerjaan.' },
  { id: 'csat', label: 'Kepuasan', satuan: 'skor', basis: 'ambil', kelompok: 'Mutu', naikBaik: true, jelas: 'Rata-rata bintang dari survei pelanggan (1–5).' },
];

export const metrikById = (id: MetrikId): DefMetrik => METRIK.find((m) => m.id === id) ?? METRIK[0]!;

// ---------------------------------------------------------------------------------------------
// Sumbu waktu

const hariISO = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const BLN = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

/** Senin sebagai awal minggu — sama dengan cara toko menghitung minggu kerja. */
function awalMinggu(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  const geser = (d.getDay() + 6) % 7;                 // Sen=0 … Min=6
  d.setDate(d.getDate() - geser);
  return hariISO(d);
}

/** Kunci ember periode untuk sebuah tanggal. */
export function kunciPeriode(iso: string, p: PeriodeId): string {
  if (p === 'tahun') return iso.slice(0, 4);
  if (p === 'kuartal') return `${iso.slice(0, 4)}-K${Math.floor((Number(iso.slice(5, 7)) - 1) / 3) + 1}`;
  if (p === 'bulan') return iso.slice(0, 7);
  if (p === 'minggu') return awalMinggu(iso);
  return iso;
}

/** Label yang dibaca orang. Mingguan memakai tanggal awal minggunya. */
export function labelPeriode(kunci: string, p: PeriodeId): string {
  if (p === 'tahun') return kunci;
  if (p === 'kuartal') { const [y, k] = kunci.split('-'); return `${k} ${y}`; }
  if (p === 'bulan') {
    const [y, m] = kunci.split('-');
    return `${BLN[Number(m) - 1] ?? m} ${y}`;
  }
  const [, m, d] = kunci.split('-');
  const teks = `${Number(d)} ${BLN[Number(m) - 1] ?? m}`;
  return p === 'minggu' ? `${teks} –` : teks;
}

/** Seluruh ember dari `dari` sampai `sampai`, termasuk yang kosong. */
export function deretPeriode(dari: string, sampai: string, p: PeriodeId): string[] {
  const out: string[] = [];
  if (!dari || !sampai || dari > sampai) return out;
  if (p === 'tahun') {
    for (let y = Number(dari.slice(0, 4)); y <= Number(sampai.slice(0, 4)) && out.length < 100; y++) out.push(String(y));
    return out;
  }
  if (p === 'kuartal') {
    let y = Number(dari.slice(0, 4)), k = Math.floor((Number(dari.slice(5, 7)) - 1) / 3) + 1;
    const yA = Number(sampai.slice(0, 4)), kA = Math.floor((Number(sampai.slice(5, 7)) - 1) / 3) + 1;
    while ((y < yA || (y === yA && k <= kA)) && out.length < 200) {
      out.push(`${y}-K${k}`);
      k += 1; if (k > 4) { k = 1; y += 1; }
    }
    return out;
  }
  if (p === 'bulan') {
    let y = Number(dari.slice(0, 4)), m = Number(dari.slice(5, 7));
    const yA = Number(sampai.slice(0, 4)), mA = Number(sampai.slice(5, 7));
    while (y < yA || (y === yA && m <= mA)) {
      out.push(`${y}-${String(m).padStart(2, '0')}`);
      m += 1; if (m > 12) { m = 1; y += 1; }
      if (out.length > 600) break;
    }
    return out;
  }
  const langkah = p === 'minggu' ? 7 : 1;
  let cur = p === 'minggu' ? awalMinggu(dari) : dari;
  const batas = sampai;
  while (cur <= batas && out.length <= 800) {
    out.push(cur);
    const d = new Date(cur + 'T00:00:00');
    d.setDate(d.getDate() + langkah);
    cur = hariISO(d);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Perhitungan metrik per kelompok tiket

const selisihHari = (a: string, b: string): number =>
  Math.max(0, Math.round((Date.parse(b + 'T00:00:00') - Date.parse(a + 'T00:00:00')) / 86400000));

const bagi = (atas: number, bawah: number): number | null => (bawah > 0 ? atas / bawah : null);
const bulat2 = (v: number | null): number | null => (v == null ? null : Math.round(v * 100) / 100);

/** Nilai satu metrik untuk sekumpulan tiket yang jatuh di satu ember periode. */
export function hitungMetrik(tk: TiketTren[], id: MetrikId): number | null {
  const omzet = () => tk.reduce((a, x) => a + omzetTiket(x), 0);
  const modal = () => tk.reduce((a, x) => a + modalTiket(x), 0);
  switch (id) {
    case 'omzet': return omzet();
    case 'laba': return omzet() - modal();
    case 'margin': return bulat2(bagi((omzet() - modal()) * 100, omzet()));
    case 'aov': return bulat2(bagi(omzet(), tk.length));
    case 'piutang': return tk.reduce((a, x) => a + piutangTiket(x), 0);
    case 'masuk': return tk.length;
    case 'selesai': return tk.length;
    case 'lama': {
      const l = tk.filter((x) => x.tglISO && x.tglAmbilISO).map((x) => selisihHari(x.tglISO!, x.tglAmbilISO!));
      return bulat2(bagi(l.reduce((a, b) => a + b, 0), l.length));
    }
    case 'gagal': return bulat2(bagi(tk.filter((x) => x.hasil === 'tidakbisa' || x.hasil === 'batal').length * 100, tk.length));
    case 'ulang': return bulat2(bagi(tk.filter((x) => x.servisUlang === true).length * 100, tk.length));
    case 'csat': {
      const r = tk.map((x) => x.rating).filter((v): v is number => typeof v === 'number' && v > 0);
      return bulat2(bagi(r.reduce((a, b) => a + b, 0), r.length));
    }
  }
}

/** Metrik yang tak punya arti bila tak ada satu pun tiket di periode itu (rata-rata & persentase). */
const BUTUH_ISI: MetrikId[] = ['margin', 'aov', 'lama', 'gagal', 'ulang', 'csat'];

// ---------------------------------------------------------------------------------------------
// Pemecahan seri

const MAKS_SERI = 5;   // di atas ini garis saling menutupi dan grafiknya berhenti menjelaskan

function namaPecah(t: TiketTren, p: PecahId): string {
  if (p === 'cabang') return t.outlet || '(tanpa cabang)';
  if (p === 'teknisi') return t.teknisi?.trim() || '(belum ditugaskan)';
  if (p === 'tipe') return t.kat === 'Toko' ? 'Toko' : t.kat === 'User' ? 'User' : '(tak diketahui)';
  return 'Semua';
}

export interface OpsiTren {
  metrik: MetrikId; pecah: PecahId; periode: PeriodeId;
  dari: string; sampai: string;
}
export interface SeriTren { nama: string; nilai: (number | null)[] }
export interface HasilTren { label: string[]; kunci: string[]; seri: SeriTren[]; def: DefMetrik }

export function bangunTren(tiket: TiketTren[], o: OpsiTren): HasilTren {
  const def = metrikById(o.metrik);
  const kunci = deretPeriode(o.dari, o.sampai, o.periode);
  const label = kunci.map((k) => labelPeriode(k, o.periode));
  const posisi = new Map(kunci.map((k, i) => [k, i]));

  // Kelompokkan: nama seri → indeks periode → tiket
  const ember = new Map<string, TiketTren[][]>();
  const totalPerSeri = new Map<string, number>();
  for (const t of tiket) {
    const tgl = def.basis === 'ambil' ? t.tglAmbilISO : t.tglISO;
    if (!tgl) continue;
    // Metrik berbasis-ambil hanya bicara soal unit yang BENAR-BENAR sudah diambil.
    if (def.basis === 'ambil' && t.status !== 'Sudah Diambil') continue;
    const i = posisi.get(kunciPeriode(tgl, o.periode));
    if (i === undefined) continue;
    const nama = namaPecah(t, o.pecah);
    let baris = ember.get(nama);
    if (!baris) { baris = kunci.map(() => []); ember.set(nama, baris); }
    baris[i]!.push(t);
    totalPerSeri.set(nama, (totalPerSeri.get(nama) ?? 0) + Math.abs(omzetTiket(t)) + 1);
  }

  if (o.pecah === 'total' && !ember.has('Semua')) ember.set('Semua', kunci.map(() => []));

  // Urutkan seri dari yang paling besar; sisanya digabung supaya grafik tetap terbaca.
  let nama = [...ember.keys()].sort((a, b) => (totalPerSeri.get(b) ?? 0) - (totalPerSeri.get(a) ?? 0));
  let gabung: TiketTren[][] | null = null;
  if (nama.length > MAKS_SERI) {
    const sisa = nama.slice(MAKS_SERI);
    nama = nama.slice(0, MAKS_SERI);
    gabung = kunci.map(() => []);
    for (const n of sisa) ember.get(n)!.forEach((tk, i) => gabung![i]!.push(...tk));
  }

  const keSeri = (n: string, baris: TiketTren[][]): SeriTren => ({
    nama: n,
    nilai: baris.map((tk) => (tk.length === 0 && BUTUH_ISI.includes(o.metrik) ? null : hitungMetrik(tk, o.metrik))),
  });
  const seri = nama.map((n) => keSeri(n, ember.get(n)!));
  if (gabung) seri.push(keSeri('Lainnya', gabung));
  return { label, kunci, seri, def };
}

// ---------------------------------------------------------------------------------------------
// Indeks 100 — supaya cabang besar & kecil bisa dibandingkan bentuk pertumbuhannya

export function keIndeks(nilai: (number | null)[]): (number | null)[] {
  const iBasis = nilai.findIndex((v) => v != null && v !== 0);
  if (iBasis < 0) return nilai.map(() => null);
  const basis = nilai[iBasis]!;
  return nilai.map((v, i) => (i < iBasis || v == null ? null : Math.round((v / basis) * 1000) / 10));
}

// ---------------------------------------------------------------------------------------------
// Analisa arah

export type Arah = 'naik' | 'turun' | 'datar' | 'kosong' | 'belum';
export interface Analisa { arah: Arah; teks: string; perPeriode: number | null }

const MIN_TITIK = 4;   // di bawah ini bentuk garis belum berarti apa-apa

/**
 * Arah dinilai dari KEMIRINGAN SELURUH TITIK (regresi sederhana), bukan titik terakhir dibanding
 * sebelumnya — satu periode aneh di ujung tak boleh membalik vonis seluruh tren.
 */
export function analisaSeri(nilai: (number | null)[], satuan: Satuan): Analisa {
  const titik = nilai.map((v, i) => ({ i, v })).filter((p): p is { i: number; v: number } => p.v != null);
  if (titik.length < MIN_TITIK) return { arah: 'belum', teks: 'Belum cukup periode untuk dinilai', perPeriode: null };
  if (titik.every((p) => p.v === 0)) return { arah: 'kosong', teks: 'Belum ada angka', perPeriode: null };

  const n = titik.length;
  const rerataI = titik.reduce((a, p) => a + p.i, 0) / n;
  const rerataV = titik.reduce((a, p) => a + p.v, 0) / n;
  const atas = titik.reduce((a, p) => a + (p.i - rerataI) * (p.v - rerataV), 0);
  const bawah = titik.reduce((a, p) => a + (p.i - rerataI) ** 2, 0);
  const kemiringan = bawah === 0 ? 0 : atas / bawah;

  // "Kecil" diukur relatif terhadap rata-rata deret; untuk persen & skor pakai ambang mutlak,
  // karena 1% dari margin 5% bukan hal kecil sementara 1% dari 80% memang kecil.
  const rel = rerataV !== 0 ? Math.abs(kemiringan / rerataV) : 0;
  const datar = satuan === 'pct' || satuan === 'skor' ? Math.abs(kemiringan) < 0.5 : rel < 0.02;
  if (datar) return { arah: 'datar', teks: 'Mendatar', perPeriode: kemiringan };
  const naik = kemiringan > 0;
  const persen = rerataV !== 0 ? Math.round(rel * 1000) / 10 : null;
  return {
    arah: naik ? 'naik' : 'turun',
    teks: `${naik ? 'Naik' : 'Turun'}${persen != null ? ` ±${persen}%/periode` : ''}`,
    perPeriode: kemiringan,
  };
}

// ---------------------------------------------------------------------------------------------
// Periode berjalan

export interface InfoBerjalan { lewat: number; total: number; proyeksi: number | null }

/**
 * Periode terakhir sering terlihat anjlok padahal hanya belum selesai. Ini menghitung sudah
 * berapa lama periode itu berjalan, dan berapa nilainya kalau lajunya bertahan.
 */
export function infoBerjalan(p: PeriodeId, kunciTerakhir: string, kini: Date, nilaiTerakhir: number | null): InfoBerjalan | null {
  const hariIni = hariISO(kini);
  if (p === 'hari') return null;                        // hari berjalan tak perlu proyeksi
  const laju = (lewat: number, total: number): InfoBerjalan =>
    ({ lewat, total, proyeksi: nilaiTerakhir != null && lewat > 0 ? Math.round((nilaiTerakhir / lewat) * total) : null });
  if (p === 'tahun') {
    if (kunciTerakhir !== hariIni.slice(0, 4)) return null;
    const awal = `${kini.getFullYear()}-01-01`;
    const total = selisihHari(awal, `${kini.getFullYear()}-12-31`) + 1;
    return laju(selisihHari(awal, hariIni) + 1, total);
  }
  if (p === 'kuartal') {
    if (kunciTerakhir !== kunciPeriode(hariIni, 'kuartal')) return null;
    const kIdx = Math.floor(kini.getMonth() / 3);                       // 0..3
    const awalD = new Date(kini.getFullYear(), kIdx * 3, 1);
    const akhirD = new Date(kini.getFullYear(), kIdx * 3 + 3, 0);       // hari terakhir kuartal
    return laju(selisihHari(hariISO(awalD), hariIni) + 1, selisihHari(hariISO(awalD), hariISO(akhirD)) + 1);
  }
  if (p === 'bulan') {
    if (kunciTerakhir !== hariIni.slice(0, 7)) return null;
    const total = new Date(kini.getFullYear(), kini.getMonth() + 1, 0).getDate();
    const lewat = kini.getDate();
    return { lewat, total, proyeksi: nilaiTerakhir != null && lewat > 0 ? Math.round((nilaiTerakhir / lewat) * total) : null };
  }
  const awal = awalMinggu(hariIni);
  if (kunciTerakhir !== awal) return null;
  const lewat = selisihHari(awal, hariIni) + 1;
  return { lewat, total: 7, proyeksi: nilaiTerakhir != null ? Math.round((nilaiTerakhir / lewat) * 7) : null };
}

// ---------------------------------------------------------------------------------------------
// Membandingkan beberapa indikator dalam satu grafik

export interface OpsiMulti { metrik: MetrikId[]; periode: PeriodeId; dari: string; sampai: string }

/**
 * Satuan yang dipakai bersama beberapa indikator, atau null bila campur.
 *
 * Null itu penting: menggambar Rupiah dan persen pada satu sumbu membuat 300.000 dan 75% terlihat
 * sebanding padahal tidak. Pemanggil memakai ini untuk memaksa mode Indeks 100 — di sana yang
 * dibandingkan bentuk pertumbuhannya, bukan besarnya, dan itu memang sah.
 */
export function satuanSeragam(ids: MetrikId[]): Satuan | null {
  if (!ids.length) return null;
  const s = new Set(ids.map((id) => metrikById(id).satuan));
  return s.size === 1 ? [...s][0]! : null;
}

/**
 * Satu garis per INDIKATOR (bukan per cabang/teknisi). Tiap indikator tetap memakai basis
 * tanggalnya sendiri — Unit Masuk ikut tanggal terima, Omzet ikut tanggal ambil — supaya
 * membandingkan "berapa masuk vs berapa jadi uang" tidak diam-diam menggeser salah satunya.
 */
export function bangunMulti(tiket: TiketTren[], o: OpsiMulti): HasilTren {
  const kunci = deretPeriode(o.dari, o.sampai, o.periode);
  const label = kunci.map((k) => labelPeriode(k, o.periode));
  const seri: SeriTren[] = o.metrik.map((id) => {
    const satu = bangunTren(tiket, { metrik: id, pecah: 'total', periode: o.periode, dari: o.dari, sampai: o.sampai });
    return { nama: metrikById(id).label, nilai: satu.seri[0]?.nilai ?? kunci.map(() => null) };
  });
  return { label, kunci, seri, def: metrikById(o.metrik[0] ?? 'omzet') };
}

// ---------------------------------------------------------------------------------------------
// Membandingkan dua periode (mis. Agustus vs Juli)

export type JenisBanding = 'bulan' | 'kuartal' | 'tahun';
export interface OpsiBanding { metrik: MetrikId; jenis: JenisBanding; a: string; b: string }

/** Periode yang benar-benar punya data, terbaru dulu — untuk mengisi pilihan pembanding. */
export function periodeTersedia(tiket: TiketTren[], jenis: JenisBanding, basis: 'terima' | 'ambil'): string[] {
  const set = new Set<string>();
  for (const t of tiket) {
    const tgl = basis === 'ambil' ? t.tglAmbilISO : t.tglISO;
    if (tgl) set.add(kunciPeriode(tgl, jenis));
  }
  return [...set].sort().reverse();
}

/** Berapa langkah dalam satu periode, dan label tiap langkahnya. */
function langkahBanding(kunci: string, jenis: JenisBanding): { label: string[]; batas: string[]; awal: string[] } {
  if (jenis === 'bulan') {
    const y = Number(kunci.slice(0, 4)), m = Number(kunci.slice(5, 7));
    const hari = new Date(y, m, 0).getDate();
    const tgl = Array.from({ length: hari }, (_, i) => `${kunci}-${String(i + 1).padStart(2, '0')}`);
    return { label: Array.from({ length: hari }, (_, i) => String(i + 1)), batas: tgl, awal: tgl };
  }
  // Kuartal & tahun memakai sumbu BULAN — sumbu harian untuk setahun (366 titik) tak terbaca.
  const y = Number(kunci.slice(0, 4));
  const mulai = jenis === 'kuartal' ? (Number(kunci.slice(6)) - 1) * 3 : 0;
  const jml = jenis === 'kuartal' ? 3 : 12;
  return {
    label: Array.from({ length: jml }, (_, i) => BLN[mulai + i] ?? ''),
    batas: Array.from({ length: jml }, (_, i) => {
      const bulan = mulai + i + 1;
      return `${y}-${String(bulan).padStart(2, '0')}-${String(new Date(y, bulan, 0).getDate()).padStart(2, '0')}`;
    }),
    awal: Array.from({ length: jml }, (_, i) => `${y}-${String(mulai + i + 1).padStart(2, '0')}-01`),
  };
}

/**
 * Dua periode digambar KUMULATIF pada sumbu relatif (tanggal 1..31, atau bulan ke-1..3).
 *
 * Kumulatif karena pertanyaannya memang "sampai tanggal segini, bulan ini lebih baik dari bulan
 * lalu tidak?" — dan itu tak terjawab oleh angka harian yang naik-turun.
 *
 * Rata-rata & persentase TIDAK ditumpuk (75% + 80% bukan 155%): nilainya dihitung ulang dari
 * seluruh tiket sampai hari itu.
 */
export function bandingPeriode(tiket: TiketTren[], o: OpsiBanding, kini = new Date()): HasilTren {
  const def = metrikById(o.metrik);
  const { label, batas } = langkahBanding(o.a, o.jenis);
  const hariIni = hariISO(kini);

  const seriUntuk = (kunci: string): SeriTren => {
    const milik = tiket.filter((t) => {
      const tgl = def.basis === 'ambil' ? t.tglAmbilISO : t.tglISO;
      if (!tgl || kunciPeriode(tgl, o.jenis) !== kunci) return false;
      return def.basis !== 'ambil' || t.status === 'Sudah Diambil';
    });
    const lk = langkahBanding(kunci, o.jenis);
    return {
      nama: labelPeriode(kunci, o.jenis),
      nilai: lk.batas.map((sampai, i) => {
        // Langkah yang BELUM DIMULAI tak digambar — meneruskan garis mendatar sampai akhir bulan
        // membuat periode berjalan terlihat "mandek", padahal harinya memang belum tiba.
        if ((lk.awal[i] ?? sampai) > hariIni) return null;
        // Langkah yang sedang berjalan dihitung sampai HARI INI, bukan sampai akhir langkahnya.
        const batas = sampai > hariIni ? hariIni : sampai;
        const sd = milik.filter((t) => {
          const tgl = (def.basis === 'ambil' ? t.tglAmbilISO : t.tglISO)!;
          return tgl <= batas;
        });
        return hitungMetrik(sd, o.metrik);
      }),
    };
  };

  const seri = [seriUntuk(o.a), seriUntuk(o.b)];
  // Sumbu memakai periode terpanjang supaya Februari vs Januari tak terpotong.
  const panjang = Math.max(...seri.map((s) => s.nilai.length), label.length);
  for (const s of seri) while (s.nilai.length < panjang) s.nilai.push(null);
  const labelPenuh = panjang > label.length && o.jenis === 'bulan'
    ? Array.from({ length: panjang }, (_, i) => String(i + 1)) : label;

  return { label: labelPenuh, kunci: batas, seri, def };
}
